// modules
import React, {useEffect, useState, useContext} from "react";
import "../styles/DashboardPage.scss";
import InfoCard from "../components/InfoCard";
import SkeletonCard from "../components/SkeletonCard";
import Heatmap from "../components/Heatmap";
import ApiService from "../services/ApiService.js";
import {GlobalContext} from "../context/GlobalContext";
import RangeSelector from "../components/RangeSelector.jsx";
import Country from "../components/Country.jsx";
import ReportsDropdown from "../components/ReportsDropdown.jsx";
import LineChart from "../components/LineChart.jsx";
import {useAuth} from "react-oidc-context";
import BarChart from "../components/BarChart.jsx";

const DashboardPage = () => {
    const auth = useAuth();
    const {state, dispatch} = useContext(GlobalContext);
    const {allReleases, latestRelease, rangeReleases, selectedTimeRange, isLoading, error} =
        state;


    useEffect(() => {
        // only fetch once the user is authenticated
        if (!auth.isAuthenticated) return;

        const fetchData = async () => {
            dispatch({type: "FETCH_START"});
            try {
                // react-oidc-context puts the ID token on user.id_token
                const accessToken = auth.user?.access_token;
                const res = await ApiService.getAllReleasesByDateRange(
                    null,
                    null,
                    accessToken
                );
                dispatch({type: "ALL_RELEASES", payload: res});
                dispatch({type: "LATEST_RELEASE", payload: res[res.length - 1]});
                dispatch({type: "FETCH_SUCCESS"});
            } catch (err) {
                console.error("Failed to load data:", err);
                dispatch({
                    type: "FETCH_ERROR",
                    payload: "Failed to load dashboard data. Please try refreshing the page."
                });
            }
        };

        fetchData();
    }, [auth.isAuthenticated, auth.user, dispatch]);


    const formattedData = rangeReleases.map((r) => ({
        // reuse the same date formatting you had on the LineChart
        formattedDate: new Date(r.CreationTimestamp).toLocaleDateString(
            "en-GB",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
        ),

        id: r.Id,
        isMapRelease: r.IsMapRelease,
        status: r.Status,
        CreationTimestamp: r.CreationTimestamp,
        ApprovalDecisionTimestamp: r.ApprovalDecisionTimestamp,
        PublishTimestamp: r.PublishTimestamp,
        mapCreationPipelineVersion: r.MapCreationPipelineVersion,
        totalPublicFacilities: r.TotalPublicFacilities,
        totalCustomerReleaseMapFacilities: r.TotalCustomerReleaseMapFacilities,
        totalPreReleaseMapFacilities: r.TotalPreReleaseMapFacilities,
        totalDeactivatedFacilities: r.TotalDeactivatedFacilities,
        totalMissingPoiFacilities: r.TotalMissingPoiFacilities,
        totalTraceKilometersPublicFacilities: r.TotalTraceKilometersPublicFacilities,
        totalCustomerReleaseParkingSlots: r.TotalCustomerReleaseParkingSlots,
        totalCustomerReleaseChargingSlots: r.TotalCustomerReleaseChargingSlots,
        totalCustomerReleaseEntrances: r.TotalCustomerReleaseEntrances,
        totalMapPartitions: r.TotalMapPartitions,
        totalUpdatedMapPartitions: r.TotalUpdatedMapPartitions,
        totalSizeUpdatedMapPartitions: r.TotalSizeUpdatedMapPartitions,
        totalMetadataPartitions: r.TotalMetadataPartitions,
        totalUpdatedMetadataPartitions: r.TotalUpdatedMetadataPartitions,
        totalSizeUpdatedMetadataPartitions: r.TotalSizeUpdatedMetadataPartitions,
        averageLocalizationSuccessRate: r.AverageLocalizationSuccessRate,
        averageFunctionAbortRate: r.AverageFunctionAbortRate,
    }));


    return (
        <div className="db-main-container">
            {error && (
                <div className="db-error-banner">
                    <span>{error}</span>
                </div>
            )}
            <Heatmap releaseId={rangeReleases?.[rangeReleases.length - 1]?.ID} isLoading={isLoading}/>
            <Country/>
            <ReportsDropdown/>
            <RangeSelector/>
            <p className="db-title">Indoor Parking Facilities</p>
            <div className="db-ic-div">
                {isLoading ? (
                    <>
                        <SkeletonCard/><SkeletonCard/><SkeletonCard/><SkeletonCard/><SkeletonCard/>
                    </>
                ) : (
                    <>
                        <InfoCard
                            showDelta={true}
                            dataTitle="Public Facilities"
                            dataSubTitle="Number of identified public parking facilities"
                            currentData={
                                rangeReleases?.[rangeReleases.length - 1]?.TotalPublicFacilities ??
                                null
                            }
                            historicalData={rangeReleases?.[0]?.TotalPublicFacilities ?? null}
                            timeRange={selectedTimeRange}
                            hasCustomerMaps={true}
                        ></InfoCard>
                        <InfoCard
                            showDelta={true}
                            dataTitle="Mapped Facilities"
                            dataSubTitle="Number of parking facilities with customer-released map"
                            currentData={
                                rangeReleases?.[rangeReleases.length - 1]
                                    ?.TotalCustomerReleaseMapFacilities ?? null
                            }
                            historicalData={
                                rangeReleases?.[0]?.TotalCustomerReleaseMapFacilities ?? null
                            }
                            timeRange={selectedTimeRange}
                            hasCustomerMaps={true}
                        ></InfoCard>
                        <InfoCard
                            showDelta={true}
                            dataTitle="Pre-Mapped Facilities"
                            dataSubTitle="Number of parking facilities with pre-released map"
                            currentData={
                                rangeReleases?.[rangeReleases.length - 1]
                                    ?.TotalPreReleaseMapFacilities ?? null
                            }
                            historicalData={
                                rangeReleases?.[0]?.TotalPreReleaseMapFacilities ?? null
                            }
                            timeRange={selectedTimeRange}
                            hasCustomerMaps={true}
                        ></InfoCard>
                        <InfoCard
                            showDelta={true}
                            dataTitle="Descoped Facilities"
                            dataSubTitle="Number of closed or descoped parking facilities"
                            currentData={
                                rangeReleases?.[rangeReleases.length - 1]
                                    ?.TotalDeactivatedFacilities ?? null
                            }
                            historicalData={
                                rangeReleases?.[0]?.TotalDeactivatedFacilities ?? null
                            }
                            timeRange={selectedTimeRange}
                            hasCustomerMaps={true}
                        ></InfoCard>
                        <InfoCard
                            showDelta={true}
                            dataTitle="Missing POIs"
                            dataSubTitle="Total number of parking facilities with missing external POI information"
                            currentData={
                                rangeReleases?.[rangeReleases.length - 1]
                                    ?.TotalMissingPoiFacilities ?? null
                            }
                            historicalData={rangeReleases?.[0]?.TotalMissingPoiFacilities ?? null}
                            timeRange={selectedTimeRange}
                            hasCustomerMaps={true}
                        ></InfoCard>
                    </>
                )}
            </div>

            <div className="db-chart-flex">
                <LineChart
                    yAxisTitle="Number"
                    chartTitle=""
                    linesConfig={[
                        {
                            key: "TotalPublicFacilities",
                            name: "Public Facilities",
                            color: "#8884d8",
                        },
                        {
                            key: "TotalCustomerReleaseMapFacilities",
                            name: "Mapped Facilities",
                            color: "#82ca9d",
                        },
                    ]}
                />
                <LineChart
                    chartTitle=""
                    yAxisTitle="Number"
                    linesConfig={[
                        {
                            key: "TotalPreReleaseMapFacilities",
                            name: "Pre-Mapped Facilities",
                            color: "#8884d8",
                        },
                        {
                            key: "TotalDeactivatedFacilities",
                            name: "Descoped Facilities",
                            color: "#82ca9d",
                        },
                        {
                            key: "TotalMissingPoiFacilities",
                            name: "Missing POIs",
                            color: "#ffc658",
                        },
                    ]}
                />
            </div>


            <p className="db-title">Partitions and Data Delivery</p>


            <div className="db-ic-div">
                {isLoading ? (
                    <div className="db-boxs-row">
                        <SkeletonCard/><SkeletonCard/><SkeletonCard/><SkeletonCard/>
                    </div>
                ) : (
                    <>
                        <div className="db-boxs-row">
                            <InfoCard
                                showDelta={true}
                                dataTitle="Metadata Partitions"
                                dataSubTitle="Number of partitions in the metadata layer"
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.TotalMetadataPartitions ?? null
                                }
                                historicalData={rangeReleases?.[0]?.TotalMetadataPartitions ?? null}
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                            <InfoCard
                                showDelta={false}
                                dataTitle="Updated Metadata Partitions"
                                dataSubTitle="Number of metadata partitions that have changed since the last release"
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.TotalUpdatedMetadataPartitions ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.TotalUpdatedMetadataPartitions ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                            <InfoCard
                                showDelta={true}
                                dataTitle="Map partitions"
                                dataSubTitle="Number of partitions in the map layer"
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]?.TotalMapPartitions ??
                                    null
                                }
                                historicalData={rangeReleases?.[0]?.TotalMapPartitions ?? null}
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                            <InfoCard
                                showDelta={false}
                                dataTitle="Updated Map Partitions"
                                dataSubTitle="Number of map partitions that have changed since the last release"
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.TotalUpdatedMapPartitions ?? null
                                }
                                historicalData={rangeReleases?.[0]?.TotalUpdatedMapPartitions ?? null}
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                        </div>
                        <div className="db-boxs-row">

                            <InfoCard
                                showDelta={false}
                                unit="bytes"
                                dataTitle="Updated Metadata Size"
                                dataSubTitle="Total size of updated metadata partitions "
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.TotalSizeUpdatedMetadataPartitions ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.TotalSizeUpdatedMetadataPartitions ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                            <InfoCard
                                unit="bytes"
                                showDelta={false}
                                dataTitle="Metadata Partition Size"
                                dataSubTitle="Average size of a single map partition "
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.AverageSizeUpdatedMetadataPartition ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.AverageSizeUpdatedMetadataPartition ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>

                            <InfoCard
                                showDelta={false}
                                unit="bytes"
                                dataTitle="Updated Map Size"
                                dataSubTitle="Total size of updated map partitions "
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.TotalSizeUpdatedMapPartitions ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.TotalSizeUpdatedMapPartitions ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>

                            <InfoCard
                                showDelta={false}
                                unit="bytes"
                                dataTitle="Map Partition Size"
                                dataSubTitle="Total size of updated map partitions "
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.AverageSizeUpdatedMapPartition ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.AverageSizeUpdatedMapPartition ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>

                        </div>

                    </>
                )}
            </div>

            <div className="db-chart-flex">
                <BarChart
                    yAxisTitle="Number"
                    chartTitle=""
                    data={formattedData}
                    xKey="formattedDate"
                    barsConfig={[
                        {
                            key: "totalMetadataPartitions",
                            name: "Metadata Partitions",
                            stackId: "meta",
                        },
                        {
                            key: "totalUpdatedMetadataPartitions",
                            name: "Updated Metadata Partitions",
                            stackId: "meta2",
                        },
                        {
                            key: "totalMapPartitions",
                            name: "Map Partitions",
                            stackId: "map",
                        },
                        {
                            key: "totalUpdatedMapPartitions",
                            name: "Updated Map Partitions",
                            stackId: "map2",
                        },
                    ]}
                />

                <BarChart
                    unit="bytes"
                    chartTitle=""
                    yAxisTitle="Size (bytes)"
                    data={formattedData}
                    xKey="formattedDate"
                    barsConfig={[
                        {
                            key: "totalSizeUpdatedMapPartitions",
                            name: "Updated Map Size",
                            stackId: "map",
                        },
                        {
                            key: "totalSizeUpdatedMetadataPartitions",
                            name: "Updated Metadata Size",
                            stackId: "meta",
                        },
                    ]}
                />
            </div>


            <p className="db-title">AVP Map Content</p>


            <div className="db-ic-div">
                {isLoading ? (
                    <div className="db-boxs-row">
                        <SkeletonCard/><SkeletonCard/><SkeletonCard/><SkeletonCard/>
                    </div>
                ) : (
                    <>
                        <div className="db-boxs-row">
                            <InfoCard
                                showDelta={true}
                                dataTitle="Trace Kilometers"
                                dataSubTitle="Total length of collected trace kilometers in public parking facilities"
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.TotalTraceKilometersPublicFacilities ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.TotalTraceKilometersPublicFacilities ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                            <InfoCard
                                showDelta={true}
                                dataTitle="Parking Slots"
                                dataSubTitle="Total number of parking slots in customer-released maps"
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.TotalCustomerReleaseParkingSlots ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.TotalCustomerReleaseParkingSlots ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                            <InfoCard
                                showDelta={true}
                                dataTitle="Charging Slots"
                                dataSubTitle="Total number of charging slots in customer-released maps"
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.TotalCustomerReleaseChargingSlots ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.TotalCustomerReleaseChargingSlots ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                            <InfoCard
                                showDelta={true}
                                dataTitle="Entrances"
                                dataSubTitle="Total number of identified entrances in public parking facilities"
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.TotalCustomerReleaseEntrances ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.TotalCustomerReleaseEntrances ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                        </div>
                        <div className="db-boxs-row">
                            <InfoCard
                                showDelta={false}
                                dataTitle="Average trace kilometers"
                                dataSubTitle="Average length of collected trace kilometers per public parking facility"
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.AverageTraceKilometersPublicFacility ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.AverageTraceKilometersPublicFacility ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                            <InfoCard
                                showDelta={false}
                                dataTitle="Average Parking Slots"
                                dataSubTitle="Average number of parking slots per customer-released map"
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.AverageCustomerReleaseParkingSlots ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.AverageCustomerReleaseParkingSlots ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                            <InfoCard
                                showDelta={false}
                                dataTitle="Average Charging Slots"
                                dataSubTitle="Average number of charging slots per customer-released map"
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.AverageCustomerReleaseChargingSlots ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.AverageCustomerReleaseChargingSlots ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                            <InfoCard
                                showDelta={false}
                                dataTitle="Average Parking Entrances"
                                dataSubTitle="Average number of identified entrances per public parking facility"
                                currentData={
                                    rangeReleases?.[rangeReleases.length - 1]
                                        ?.AverageCustomerReleaseEntrances ?? null
                                }
                                historicalData={
                                    rangeReleases?.[0]?.AverageCustomerReleaseEntrances ?? null
                                }
                                timeRange={selectedTimeRange}
                                hasCustomerMaps={true}
                            ></InfoCard>
                        </div>
                    </>
                )}
            </div>

            <div className="db-chart-flex">
                <LineChart
                    yAxisTitle="Total length (Km)"
                    chartTitle=""
                    linesConfig={[
                        {
                            key: "TotalTraceKilometersPublicFacilities",
                            name: "Trace Kilometers",
                            color: "#8884d8",
                        },
                    ]}
                />
                <LineChart
                    yAxisTitle="Total number"
                    chartTitle=""
                    linesConfig={[
                        {
                            key: "TotalCustomerReleaseParkingSlots",
                            name: "Parking Slots",
                            color: "#8884d8",
                        },
                    ]}
                />
                <LineChart
                    yAxisTitle="Total number"
                    chartTitle=""
                    linesConfig={[
                        {
                            key: "TotalCustomerReleaseChargingSlots",
                            name: "Charging Slots",
                            color: "#8884d8",
                        },
                    ]}
                />
                <LineChart
                    yAxisTitle="Total number"
                    chartTitle=""
                    linesConfig={[
                        {
                            key: "TotalCustomerReleaseEntrances",
                            name: "Entrances",
                            color: "#8884d8",
                        },
                    ]}
                />
            </div>


            <p className="db-title">AVP Driving Function Performance</p>


            <div className="db-ic-div">
                {isLoading ? (
                    <>
                        <SkeletonCard/><SkeletonCard/>
                    </>
                ) : (
                    <>
                        <InfoCard
                            showDelta={false}
                            dataTitle="Localization Success Rate"
                            dataSubTitle="Average localization success rate in all customer-released maps"
                            currentData={
                                rangeReleases?.[rangeReleases.length - 1]
                                    ?.AverageLocalizationSuccessRate ?? null
                            }
                            historicalData={
                                rangeReleases?.[0]?.AverageLocalizationSuccessRate ?? null
                            }
                            timeRange={selectedTimeRange}
                            hasCustomerMaps={true}
                        ></InfoCard>
                        <InfoCard
                            showDelta={false}
                            dataTitle="Average Abort Rate"
                            dataSubTitle="Average function abort rate in all customer-released maps"
                            currentData={
                                rangeReleases?.[rangeReleases.length - 1]
                                    ?.AverageFunctionAbortRate ?? null
                            }
                            historicalData={rangeReleases?.[0]?.AverageFunctionAbortRate ?? null}
                            timeRange={selectedTimeRange}
                            hasCustomerMaps={true}
                        ></InfoCard>
                    </>
                )}
            </div>
            <div className="db-chart-flex">
                <LineChart
                    yAxisTitle="Rate"
                    yAxisDomain={[0, 1]}
                    chartTitle=""
                    linesConfig={[
                        {
                            key: "AverageLocalizationSuccessRate",
                            name: "Localization Success Rate",
                            color: "#8884d8",
                        },
                    ]}
                />
                <LineChart
                    yAxisTitle="Rate"
                    yAxisDomain={[0, 1]}
                    chartTitle=""
                    linesConfig={[
                        {
                            key: "AverageFunctionAbortRate",
                            name: "Function Abort Rate",
                            color: "#8884d8",
                        },
                    ]}
                />
            </div>
        </div>
    );
};

export default DashboardPage;